import React, { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { PageSizeType, TemplatesTypes, PageOrientation } from "../templates";
import { useKeyboardShortcuts, useIsMobile, useIsTablet } from "../../Hook";
import { SHORTCUTS, PAGE_SIZE_OPTIONS } from "../../Constant";
import {
  useWhatsAppConnected,
} from "../../Services";
import { useInvoiceSharing } from "./useInvoiceSharing";
import { useInvoiceData } from "./useInvoiceData";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { useInvoiceDocument } from "./useInvoiceDocument";
import { CourierParcelDrawer } from "../CourierParcelDrawer/CourierParcelDrawer";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";
import { PrintConfigSelector } from "../PrintConfigSelector/PrintConfigSelector";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";
import { useCart } from "../../Context";
import { InvoiceDrawerFooter } from "./InvoiceDrawerFooter";

interface InvoiceModalProps {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  open,
  orderId,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [pageSize, setPageSize] = useState<PageSizeType>("80mm");
  const [orientation, setOrientation] = useState<PageOrientation>("portrait");
  const [template, setTemplate] = useState<TemplatesTypes>(
    TemplatesTypes.Classic,
  );
  const { billType, orientation: savedOrientation } = useCart();

  const { order, config } = useInvoiceData({ open, orderId });
  const { getInvoiceHtml, printInvoice, downloadInvoice, getInvoicePdfBase64 } =
    useInvoiceDocument({
      order,
      config,
      pageSize,
      orientation,
      template,
    });

  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  const [courierOpen, setCourierOpen] = useState(false);
  const { isWhatsAppEnabled, isSmsGatewayEnable } = useSystemConfig();
  const isWhatsAppConnected = useWhatsAppConnected();

  const {
    handleSendWhatsAppPdf,
    handleSendWhatsAppLink: sendWhatsAppLink,
    handleSendSMSLink: sendSMSLink,
    isSendWhatsAppPending,
    isSendWhatsAppLinkPending,
    isSendSmsLinkPending,
  } = useInvoiceSharing();

  useEffect(() => {
    if (open && order) {
      const phoneVal = String(
        order.customer_phone ||
          (order as any)?.customer?.phone ||
          (order as any)?.customer?.contact_no ||
          config.whatsapp ||
          "",
      ).trim();
      setWhatsappPhone(phoneVal);
    }
  }, [open, order, config]);

  const handleSendWhatsApp = () =>
    handleSendWhatsAppPdf({
      phone: whatsappPhone,
      documentId: order?.id || "",
      documentNo: order?.order_no,
      documentLabel: "Invoice",
      getPdfBase64: getInvoicePdfBase64,
    });

  const handleSendWhatsAppLink = () =>
    sendWhatsAppLink({
      phone: whatsappPhone,
      documentNo: order?.order_no,
      documentLabel: "Invoice",
      partyType: "Customer",
      linkPath: `/${order?.id || ""}`,
    });

  const handleSendSMSLink = () =>
    sendSMSLink({
      phone: whatsappPhone,
      documentNo: order?.order_no,
      documentLabel: "Invoice",
      partyType: "Customer",
      linkPath: `/${order?.id || ""}`,
    });

  const cycleTemplate = useCallback(() => {
    setTemplate(TemplatesTypes.Classic);
  }, []);

  const cyclePageSize = useCallback(() => {
    setPageSize((prev) => {
      const idx = PAGE_SIZE_OPTIONS.findIndex((s) => s.value === prev);
      return PAGE_SIZE_OPTIONS[(idx + 1) % PAGE_SIZE_OPTIONS.length].value;
    });
  }, []);

  useKeyboardShortcuts([
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.PRINT_INVOICE,
      options: {
        enabled: open,
        enableOnFormTags: false,
        enableOnContentEditable: false,
      },
      handler: printInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.DOWNLOAD_PDF,
      options: {
        enabled: open,
        enableOnFormTags: false,
        enableOnContentEditable: false,
      },
      handler: downloadInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_TEMPLATE,
      options: {
        enabled: open,
        enableOnFormTags: false,
        enableOnContentEditable: false,
      },
      handler: cycleTemplate,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_PAGE_SIZE,
      options: {
        enabled: open,
        enableOnFormTags: false,
        enableOnContentEditable: false,
      },
      handler: cyclePageSize,
    },
  ], { enableInModalOrDrawer: true });
  
    useEffect(() => {
    if (open) {
      setPageSize(billType);
      setOrientation(savedOrientation);
    }
  }, [open, billType, savedOrientation]);


  if (!order) return null;

  return (
    <ResizableDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle("Invoice Preview")}
      width={isMobile || isTablet ? "100%" : DRAWER_FORM_WIDTH}
      className="invoice-drawer"
      destroyOnClose
      footer={
        <InvoiceDrawerFooter
          onPrint={printInvoice}
          onDownload={downloadInvoice}
          onCourier={() => setCourierOpen(true)}
          whatsappPhone={whatsappPhone}
          onWhatsappPhoneChange={setWhatsappPhone}
          onSendWhatsApp={handleSendWhatsApp}
          onSendWhatsAppLink={handleSendWhatsAppLink}
          onSendSMSLink={handleSendSMSLink}
          isSendWhatsAppPending={isSendWhatsAppPending}
          isSendWhatsAppLinkPending={isSendWhatsAppLinkPending}
          isSendSmsLinkPending={isSendSmsLinkPending}
          isWhatsAppEnabled={isWhatsAppEnabled}
          isWhatsAppConnected={isWhatsAppConnected}
          isSmsGatewayEnable={isSmsGatewayEnable}
          showShortcuts
        />
      }
    >
      <>
        <PrintConfigSelector
          template={template}
          pageSize={pageSize}
          orientation={orientation}
          onChangeTemplate={setTemplate}
          onChangePageSize={setPageSize}
          onChangeOrientation={setOrientation}
          className="flex gap-5 justify-center"
        />
        <div
          dangerouslySetInnerHTML={{ __html: getInvoiceHtml(false) }}
          style={{ width: "100%" }}
        />
      </>
      <CourierParcelDrawer
        open={courierOpen}
        order={order}
        onClose={() => setCourierOpen(false)}
      />
    </ResizableDrawer>
  );
};
